'use client';
import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Mail, Lock, User, Phone, Camera, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface RegisterFormData {
  fullName: string;
  mobile: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

export default function RegisterForm({ onSwitchToLogin }: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { signUp } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>();

  const password = watch('password');

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setProfilePreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        mobile: data.mobile,
        profilePhoto: profilePreview ?? '',
      });
      setIsSuccess(true);
    } catch (error: any) {
      const msg = error?.message ?? '';
      if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exists')) {
        toast.error('An account with this email already exists. Please sign in.');
      } else {
        toast.error(msg || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="fade-in text-center py-8">
        <div className="w-20 h-20 rounded-full bg-success/15 border-2 border-success/30 flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-3">Registration Successful!</h2>
        <div className="p-4 rounded-xl bg-warning/10 border border-warning/30 mb-6 text-left">
          <p className="text-sm font-semibold text-warning mb-1">Awaiting Admin Approval</p>
          <p className="text-sm text-muted-foreground">
            Your account is waiting for gym admin approval. You will be able to check in once your account is approved. This usually takes less than 24 hours.
          </p>
        </div>
        <button onClick={onSwitchToLogin} className="btn-primary w-full">
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-1">Create account</h2>
        <p className="text-sm text-muted-foreground">Join MuscleStation and start tracking attendance</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {/* Profile photo */}
        <div className="flex items-center gap-4">
          <div
            className="w-16 h-16 rounded-2xl bg-muted border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors duration-150 overflow-hidden flex-shrink-0"
            onClick={() => fileInputRef.current?.click()}
          >
            {profilePreview ? (
              <img src={profilePreview} alt="Profile preview" className="w-full h-full object-cover" />
            ) : (
              <Camera size={20} className="text-muted-foreground" />
            )}
          </div>
          <div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-sm font-medium text-primary hover:underline"
            >
              Upload photo
            </button>
            <p className="text-xs text-muted-foreground mt-0.5">Optional · JPG, PNG up to 5MB</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handlePhotoChange}
            aria-label="Profile photo upload"
          />
        </div>

        {/* Full Name */}
        <div>
          <label htmlFor="reg-name" className="block text-sm font-medium text-foreground mb-1.5">
            Full Name <span className="text-accent">*</span>
          </label>
          <div className="relative">
            <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id="reg-name"
              type="text"
              placeholder="Arjun Mehta"
              className={`input-field pl-10 ${errors.fullName ? 'border-accent' : ''}`}
              {...register('fullName', {
                required: 'Full name is required',
                minLength: { value: 2, message: 'Name must be at least 2 characters' },
              })}
            />
          </div>
          {errors.fullName && <p className="mt-1 text-xs text-accent">{errors.fullName.message}</p>}
        </div>

        {/* Mobile */}
        <div>
          <label htmlFor="reg-mobile" className="block text-sm font-medium text-foreground mb-1.5">
            Mobile Number <span className="text-accent">*</span>
          </label>
          <div className="relative">
            <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id="reg-mobile"
              type="tel"
              placeholder="+91 98765 43210"
              className={`input-field pl-10 ${errors.mobile ? 'border-accent' : ''}`}
              {...register('mobile', {
                required: 'Mobile number is required',
                pattern: { value: /^[\+]?[\d\s\-]{10,15}$/, message: 'Enter a valid mobile number' },
              })}
            />
          </div>
          {errors.mobile && <p className="mt-1 text-xs text-accent">{errors.mobile.message}</p>}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="reg-email" className="block text-sm font-medium text-foreground mb-1.5">
            Email Address <span className="text-accent">*</span>
          </label>
          <div className="relative">
            <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id="reg-email"
              type="email"
              placeholder="you@example.com"
              className={`input-field pl-10 ${errors.email ? 'border-accent' : ''}`}
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
              })}
            />
          </div>
          {errors.email && <p className="mt-1 text-xs text-accent">{errors.email.message}</p>}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="reg-password" className="block text-sm font-medium text-foreground mb-1.5">
            Password <span className="text-accent">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters with at least one number</p>
          <div className="relative">
            <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id="reg-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className={`input-field pl-10 pr-10 ${errors.password ? 'border-accent' : ''}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
                pattern: { value: /(?=.*[0-9])/, message: 'Password must contain at least one number' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-accent">{errors.password.message}</p>}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="reg-confirm-password" className="block text-sm font-medium text-foreground mb-1.5">
            Confirm Password <span className="text-accent">*</span>
          </label>
          <div className="relative">
            <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id="reg-confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className={`input-field pl-10 pr-10 ${errors.confirmPassword ? 'border-accent' : ''}`}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) => value === password || 'Passwords do not match',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
              aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
            >
              {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-xs text-accent">{errors.confirmPassword.message}</p>
          )}
        </div>

        <div className="pt-1">
          <p className="text-xs text-muted-foreground mb-4">
            By registering, you agree to our{' '}
            <span className="text-primary cursor-pointer hover:underline">Terms of Service</span> and{' '}
            <span className="text-primary cursor-pointer hover:underline">Privacy Policy</span>
          </p>
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3.5"
          >
            {isLoading ? (
              <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            ) : (
              <UserPlus size={18} />
            )}
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>
        </div>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Already a member?{' '}
        <button onClick={onSwitchToLogin} className="text-primary font-semibold hover:underline">
          Sign in
        </button>
      </p>
    </div>
  );
}